import { IRowData } from "@multiweb/types/contracts";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { Enum } from "OW.Remoting/Remoting/DataTypes/Enums";
import { Control } from "UI/UI.BaseControls/Control";
import { IDataSourceLoadInfo, IEditableDataSource } from "../../Global/Base/DataSources/Interfaces";
import { IEncAttrValue } from "../../Global/Base/DataTypes/IAttrValue";
import { Event } from "../../Global/Base/Events";
import { ArrayUtils } from "../../Global/Base/Utils/ArrayUtils";
import { Localization } from "../../OW.Remoting/Remoting/Localization";
import { IDataLoadingAwaitable, IValidatable, IValidationResult } from "../UI.BaseControls/ControlInterfaces";
import { registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { EditableDataRow } from "./DataGridSubcontrols/Components/EditableDataRow";
import { IDataRowElement, IEditableDataRowElement, IEditableListDataRowElement, PreviewRowClickEvent, RowClickEvent } from "./DataGridSubcontrols/Components/IDataRow";
import { EmptyDatagridMessageType } from "./DataGridSubcontrols/DataGridMessages/IEmptyDataGridMessage";
import { IEditableListDataGrid } from "./IDataGrid";
import { GlobalCustomEvent } from "./IEvent";
import { ListDataGrid } from "./ListDataGrid";

const LOAD_QUERY_MODE_EVENT = "loadDataQueryMode";
export class EditableDataGrid<R extends IEditableListDataRowElement> extends ListDataGrid<R> implements IEditableListDataGrid<R>, IDataLoadingAwaitable {
    //#region Fields
    private _isInEdit: boolean; // informace o tom, zda je datagrid v editacnim rezimu
    public editedRows: Array<R>;
    /** Element zobrazujici aktualni pocet rozeditovanych zaznamu */
    private _editedRowCountElement: HTMLElement;
    /** Vnitrni element s poctem rozeditovanych zaznamu */
    private _editedRowsCounterEl: HTMLLabelElement;
    /** Vnitrni element popiskem k pocku rozeditovanych zaznamu */
    private _numberOfEditeRowsEl: HTMLLabelElement;
    /** Pocet rozeditovanych zaznamu */
    private __editedRowCount: number;
    /** Pole ID editovanych zaznamu. Slouzi zejmena pro opetovne nastaveni isChanged jednotlivych zaznamu pri prepnuti LF z kompaktniho zobrazeni na normalni */
    private changedRowsID: Array<string> = [];

    private get _editedRowCount(): number {
        return this.__editedRowCount;
    }

    private set _editedRowCount(value: number) {
        this.__editedRowCount = value;
        this._editedRowsCounterEl.innerText = value.toString();
    }

    /** Tlacitko pro zobrazeni rozeditovanych zaznamu */
    private _displayEditedRowsButton: HTMLDivElement;
    /** Tlacitko pro zobrazeni vsech zaznamu */
    private _displayAllRowsButton: HTMLDivElement;
    /** Property, zda jsou zobrazeny jenom rozeditovane zaznamy */
    public displayedEditedRows: boolean = false;
    /** Povoluje/zakazuje zobrazovani radku s poctem rozeditovanych zaznamu */
    public displayEditedCountBar: boolean;
    /** Element pro zobrazeni query mode banneru */
    private _queryModeContainer: HTMLDivElement;
    /** Tlacitko pro nacteni vsech dat a ukonceni query mode */
    private _queryModeButton: HTMLDivElement;

    public canSwitchToEditCallback: () => boolean;

    public readonly cellValidityChanged: Event<IValidatable, { row: R; validity: IValidationResult }> = new Event<IValidatable, { row: R; validity: IValidationResult }>();

    public readonly permissionsChanged: Event<R, IPermissionChanges[]> = new Event<R, IPermissionChanges[]>();
    //#endregion

    //#region Properties
    public override get dataSource(): IEditableDataSource {
        return super.dataSource as IEditableDataSource;
    }
    public override set dataSource(value: IEditableDataSource) {
        super.dataSource = value;
    }

    public set isInEdit(v: boolean) {
        this._isInEdit = v;

        for (const item of this.selectedRows) {
            item.isInEdit = v;
        }
        if (!v) {
            //for (let item of this.EditedRows) {
            //    if (item.ID == 0) item.remove();
            //}
            this.editedRows = [];
            this._updateCountBar(0);
            this.displayCountBar(false);
        }
    }

    public get isInEdit(): boolean {
        return this._isInEdit;
    }

    public isMultiEditAllowed: boolean = true;

    protected get _allDataRows(): Array<R> {
        return [...this.dataRows ?? []];
    }

    protected override _lastSelectedDataKeys: Array<string> = [];
    /**
     * Identifikátory naposledy vybraných zeditovaných řádků
     * Neměly by se nastavovat přímo, ale v metodách _setLastSelectedDataKeys,
     * _concatenateLastSelectedDataKeys, _removeFromLastSelectedDataKeys,
     * _clearLastSelectedDataKeys!
     */
    protected _lastSelectedEditedDataKeys: Array<string> = [];

    //#endregion Properties

    protected override _constructor(): void {
        super._constructor();

        this.classList.add("editable");
        this.editedRows = new Array<R>();
        this._isInEdit = false;
        this.displayedEditedRows = false;
        this.displayEditedCountBar = true;

        this._dataRowFactory = () => new EditableDataRow() as any as R;
    }

    protected async _previewRowClick(_e: GlobalCustomEvent<PreviewRowClickEvent>, _row: R): Promise<void> {
        // const evt = e.detail;
    }

    protected override async _rowClick(e: GlobalCustomEvent<RowClickEvent>, clickedRow: R): Promise<void> {
        const evt = e.detail;
        if ((evt.originalEvent.ctrlKey || evt.originalEvent.shiftKey) && (this.isInEdit && !this.isMultiEditAllowed)) { //pokud je datagrid v editaci, neumožní označení dalších záznamů. #13107
            return;
        }

        await this.waitOnDataLoaded();

        if (!this.interactive) {
            return;
        }

        if (!(await this._checkIfCanSelectionChange(evt.originalEvent, clickedRow))) {
            return;
        }

        let dblClick = false;
        if (evt.clickCount === 0) {
            return;
        }
        if (evt.clickCount === 2) {
            dblClick = true;
        }

        let oldSelectedRows: IEditableDataRowElement[];
        if (this.isInEdit) {
            oldSelectedRows = this.selectedRows.slice(0);
        }

        const dispEvent = this._rowClickSelectItem(evt.originalEvent, clickedRow);

        let rowID: string = null;
        if (this.selectedRows.length === 1) {
            rowID = this.selectedRows[0].dataKey;
        }

        let columnID = null;
        if (dblClick || this.isInEdit) {
            const source = (evt.originalEvent.target || evt.originalEvent.srcElement) as Element;
            const tdElement = source.closest("td");
            if (tdElement && clickedRow instanceof EditableDataRow) {
                columnID = tdElement?.getAttribute("data-attrname");
            }
        }

        if (clickedRow instanceof EditableDataRow) {
            clickedRow.focusAttribute = columnID || null;
        }

        /*Zavolání události selectionChange je potřeba udělat ještě před přechodem do editace. V rámci obsloužení události se spouští task pro dočtení objektu.
            Na ten se následně čeká před přechodem do editace.*/
        if (dispEvent) {
            // pokud je vybrany jen jeden radek a zároveň nejde o už vybraný řádek
            if (rowID != null) {
                this._dispatchSelectionChangeEvent(rowID, { doubleClick: dblClick, clickedRow });
            } else { // neni vybrany zadny radek nebo oznaceno vice zaznamu
                this._dispatchSelectionChangeEvent("-1", { doubleClick: false, clickedRow });
            }
        }
        if (/*!dispEvent &&*/ dblClick) {
            this._dispatchDoubleClickEvent(true, rowID);
        }
        if (this.isInEdit) {
            // Nemůžeme porovnat přímo reference polí, ale porovnáme dataKey označených řádků
            if (!ArrayUtils.equals(this.selectedRows.map(r => r.dataKey), oldSelectedRows.map(r => r.dataKey), true)) {
                for (const sr of oldSelectedRows) {
                    if (!sr.isSelected && !sr.isChanged) {
                        sr.isInEdit = false;
                    }
                }
                await this.waitOnDataLoaded();//čekání na dočtení aktuálního objektu před přechodem do editace
                for (const sr of this.selectedRows) {
                    if (!this.canSwitchToEditCallback ||
                        (this.canSwitchToEditCallback && !this.canSwitchToEditCallback())) {
                        if (!sr.isInEdit) {
                            sr.isInEdit = true;
                        }
                    }
                }
            }
        }
    }

    /**
     * Inicializuje datagrid a preda nejaky info datasourcu
     */
    public override async initialize(): Promise<any> {
        await super.initialize();

        this._eventsController.add(this.dataSource.attributesChanged, (_sender, eventData) => {
            let selector = `:scope > [datakey='${eventData.key}']`;
            if (eventData.instanceId) {
                selector += `, :scope > [datakey='${eventData.instanceId}']`;
            }

            const el = this._dataTable.querySelector(selector) as R;

            if (!el) {
                return;
            }

            let flagColorName = "";

            if (this.flagAttributeName && eventData.values.has(this.flagAttributeName)) {
                const flagColor = AttrValueReader.read(eventData.values.get(this.flagAttributeName));
                flagColorName = flagColor instanceof Enum ? flagColor.getName() : flagColor;
                if (!this.allowedFlagColors || (this.allowedFlagColors?.includes(flagColorName))) {
                    el.flag = flagColorName;
                } else {
                    el.flag = "";
                }
            }
            for (const data of eventData.values) {
                let newVal = data["1"];
                if (newVal instanceof IEncAttrValue && !newVal.isEncrypted) { //záznam byl dešifrovaný. Měla by se použít vnitřní hodnota.
                    newVal = newVal.valueOf();
                }
                el.setChangedData(data["0"], newVal, eventData.isChanged);
                if (eventData.isChanged) {
                    const rowID = el.dataKey;
                    if (rowID && !this.changedRowsID.includes(rowID)) {
                        this.changedRowsID.push(rowID);
                    }
                }
            }
        });

        this._eventsController.add(this.dataSource.changesCancelled, (_sender, _key) => {
            this.removeIsChangedAll();
            if (this.dataRows.length === 0) {
                this._displayQueryModeBanner(false);
            } else {
                this._displayQueryModeBanner(true);
            }
            this.resizeGrid();
        });

        this._eventsController.add(this.dataSource.deleted, (_sender, payload) => {
            const key = payload.key;
            this.removeIsChangedOnKey(key);
            const el = this._listDiv.querySelector(`.DataRow:not(.frozen-row)[datakey='${key}']`);
            if (el) {
                el.remove();
            }

            this._removeRowFromEdited(key);
            this.removeRow(key);
            if (payload.maintainSelection) {
                this.selectRowOnLastDeletedPosition();
            }

            if (this.selectedRows.length == 0) {
                this._dispatchSelectionChangeEvent("-1", { doubleClick: false, clickedRow: this.lastClickedRow });
            }

            if (this.dataRows.length === 0) {
                this.emptyMessage.visible = true;
                this._displayQueryModeBanner(false);
            } else {
                this._displayQueryModeBanner(true);
            }

            this._updateEmptyMessageVisibility();
        });

        this._eventsController.add(this.dataSource.newCreated, async (_cancelableEventArgs, _sender, irow) => {
            await this.waitOnDataLoaded();
            const newRow = this.insertNewRecord(irow);
            this.initDataRowFlag(newRow, irow);
            newRow.select(true);
            newRow.isInEdit = true;
            this._displayQueryModeBanner(false);
            this.resizeGrid();
            const rowID = irow["ID"];
            if (rowID && !this.changedRowsID.includes(rowID.toString())) {
                this.changedRowsID.push(rowID.toString());
            }
        });

        this._eventsController.add(this.dataSource.saved, (_sender, irow) => {
            this.callEvent("Saved");
            this.removeIsChangedOnKey(irow.key);
            if (irow.key === irow.newKey) {
                return;
            }

            const el = this._dataTable.querySelector(`:scope > [datakey='${irow.key}']`) as R;

            if (el) {
                el.dataKey = irow.newKey;
            }
            this._displayQueryModeBanner(true);
            this.resizeGrid();
        });

        this._eventsController.add(this.dataSource.afterSaved, (_sender, eventData) => {
            this.callEvent("AfterSaved");
            for (const key of eventData.saved) {
                this.removeIsChangedOnKey(key);
            }
        });
    }

    protected override async _fetchNextBatch(eventData: IDataSourceLoadInfo): Promise<void> {
        await super._fetchNextBatch(eventData);

        if (eventData.startIndex === 0 && this._allDataRead) {
            for (const editedRow of this.editedRows) {
                if (!this.dataRows.some(row => row.dataKey === editedRow.dataKey)) {
                    ArrayUtils.remove(this.editedRows, editedRow);
                    this.decEditedRows();
                }
            }
        }
    }

    protected insertRecord(dataRow: IRowData): R {
        const dataSource = this.dataSource; // as EditableDBDataSource;

        const beforeRow = this.selectedRows.length > 0 && this.dataRows && this.dataRows.length > 0 ? (this.dataRows.indexOf(this.selectedRows[0]) > -1 ? this.selectedRows[0] : undefined) : (this.dataRows.length > 0 ? this.dataRows[0] : undefined);

        const newRow = this._dataRowFactory(this, dataRow);
        newRow.init(dataRow, this, this._createRowControlElement(newRow));
        newRow.render();
        newRow.dataKey = dataRow[dataSource.dataKey].valueOf();

        if (newRow instanceof Control) {
            newRow.registerEventListener("rowclick", (e) => {
                this._rowClick(e as RowClickEvent, newRow);
            });
        }

        newRow.cellValidityChanged.add((sender, validity) => this.cellValidityChanged.trigger(sender, { row: newRow, validity: validity }));

        if (beforeRow) {
            this._dataTable.insertBefore(newRow as HTMLElement, beforeRow as HTMLElement);
        } else {
            this._dataTable.appendChild(newRow as HTMLElement);
        }

        if (beforeRow) {
            const addIndex = this.dataRows.indexOf(beforeRow);
            this.dataRows.splice(addIndex, 0, newRow);
        } else {
            this.dataRows.push(newRow);
        }

        this._updateEmptyMessageVisibility(); // Lze volat až po přidání záznamu do dataRows

        return newRow;
    }

    public override insertExistingRecord(dataRow: IRowData): R {
        return this.insertRecord(dataRow);
    }

    public override insertNewRecord(dataRow: IRowData): R {
        const newRows = this.insertRecord(dataRow);
        const dataSource = this.dataSource;

        const keys = Object.keys(dataRow);
        for (const key of keys) {
            if (key == dataSource.dataKey || key == dataSource.permissionKey) {
                continue;
            }
            newRows.setChangedData(key, dataRow[key], true);
        }

        return newRows;
    }

    /**
     * Umožňuje čekat na dočtení dat
     */
    public async waitOnDataLoaded(): Promise<boolean> {
        for (const row of this.selectedRows) {
            for (const cell of row.dataCells) {
                if (cell instanceof IDataLoadingAwaitable) {
                    await cell.waitOnDataLoaded();
                }
            }
        }

        await this._dataSource.waitForReload();
        return Promise.resolve(true);
    }

    /**
     * Smazani oznacenych radku
     */
    public override clearSelectedRows(): void {
        for (const row of [...this.selectedRows]) { // pro jistotu je pole kopírováno, protože z originálního mohou mizet prvky
            if (row.isDisposed()) { // TODO Provizorní kontrola. Opravit příčinu práce s disposed řádky v rámci tasku 33553.
                continue;
            }

            if (!row.isChanged) {
                row.isInEdit = false;
            }
            row.isSelected = false;
        }
        this.selectedRows = [];

        this.isSelectedAll = false;
    }

    /**
     * Vytvoreni jednoho radku
     */
    protected override _createRow(data: IRowData): R {
        const dataRow = super._createRow(data);

        dataRow.cellValidityChanged.add((sender, validity) => this.cellValidityChanged.trigger(sender, { row: dataRow, validity: validity }));

        return dataRow;
    }

    protected override _setLastSelectedDataKeys(selectedRows: Array<R>): string[] {
        super._setLastSelectedDataKeys(selectedRows);
        this._lastSelectedEditedDataKeys = selectedRows.filter(row => row.isInEdit).map(row => row.dataKey);
        return this._lastSelectedDataKeys;
    }

    protected override _concatenateLastSelectedDataKeys(selectedRows: Array<R>): string[] {
        super._concatenateLastSelectedDataKeys(selectedRows);
        this._lastSelectedEditedDataKeys = selectedRows.filter(row => row.isInEdit).map(row => row.dataKey);
        return this._lastSelectedDataKeys;
    }

    protected override _removeFromLastSelectedDataKeys(dataKeyToDeselect: string): string[] {
        super._removeFromLastSelectedDataKeys(dataKeyToDeselect);
        this._lastSelectedEditedDataKeys = this._lastSelectedEditedDataKeys.filter(dataKey => dataKey !== dataKeyToDeselect);
        return this._lastSelectedDataKeys;
    }
    protected override _clearLastSelectedDataKeys(): void {
        super._clearLastSelectedDataKeys();
        this._lastSelectedEditedDataKeys = [];
    }

    public override render(): void {
        super.render();

        this._renderCountBar();
    }

    /**
     * Udrzeni vybranych radku pri refresh/razeni seznamu
     *
     * TODO Metoda pracuje s disposed řádky. Opravit příčinu práce s disposed řádky v rámci tasku 33553.
     */
    public override async keepSelectedRows(): Promise<void> {
        let IDs = new Array<any>();
        let isEdited = new Array<boolean>();
        let instanceIds = new Array<string>();
        const fromLastSelectedRows = this._lastSelectedDataKeys.length > 0;

        if (!this._selectionHelper.masterSelectAll) {
            if (this._lastSelectedDataKeys.length > 0) {
                IDs = [...this._lastSelectedDataKeys];
                // Vytvoří pole stejné délky jako IDs, kde je na odpovídajících pozicích příznak, zda byl řádek rozeditován
                isEdited = IDs.map(id => (this._lastSelectedEditedDataKeys.indexOf(id) > -1));
                instanceIds = this._lastSelectedDataKeys.filter(i => ObjectUtils.isIdTemporary(i));
            } else {
                const rows = this.selectedRows;
                for (const row of rows) {
                    let datakey = row.dataKey;
                    if (datakey) {
                        datakey = datakey.toString();
                    }
                    IDs.push(datakey);
                    isEdited.push(row.isInEdit);
                    if (datakey === row.dataset.instanceid) {
                        instanceIds.push(datakey);
                    }
                }
            }

            this.clearSelectedRows();

            if (IDs.length > 0) {
                const foundIDs: string[] = [];

                for (let i = 0; i < this.dataRows.length; i++) {
                    const dataRow = this.dataRows[i];
                    const rowDataKey = dataRow.dataKey.toString();
                    const index = IDs.indexOf(rowDataKey);
                    if (index >= 0) {
                        dataRow.isSelected = true;
                        const firstRow = dataRow.parentElement.querySelector(".DataRow");
                        if ((firstRow instanceof IDataRowElement) && firstRow.dataKey != dataRow.dataKey && this._scrollToSelectingRow) { //pokud oznacenej radek neni na prvnim miste tak k nemu doscroluju - jen pri refreshi seznamu
                            dataRow.scrollIntoView({ behavior: "auto", block: "nearest", inline: "nearest" });
                        }

                        if ((firstRow instanceof IDataRowElement) && firstRow.dataKey == dataRow.dataKey && this._scrollToSelectingRow) { //scrolovani k prvnimu radku, musi to byt zvlast kvuli uzkymu seznamu
                            firstRow.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
                        }

                        this._scrollToSelectingRow = false;
                        if (isEdited[index]) {
                            dataRow.isInEdit = true;
                        }

                        this._clearLastSelectedDataKeys();
                        foundIDs.push(rowDataKey);

                        if (this._selectionHelper.contains(rowDataKey)) {
                            dataRow.isSelected = true;
                        }
                    }
                }

                const notFoundIDs = IDs.filter(id => !foundIDs.includes(id) && !instanceIds.includes(id));

                if (notFoundIDs.length > 0) {
                    const loadedRows = await this.dataSource.loadSpecificRecords(notFoundIDs, true);
                    if (loadedRows != undefined && loadedRows.length > 0) {
                        /**
                         * Pokud dojde např. k validačním chybám, tak tyto záznamy se oproti ostatním načtou až později v loadSpecificRecords.
                         * Je potřeba informovat o dočtění, aby datasource vždy pracoval s aktuálním počtem vybraných řádků!
                         * */
                        this.dispatchSelectionChanged();
                        this._refreshSelection = false;
                        this._scrollToSelectingRow = false;
                        return;
                    } else {
                        IDs = IDs.filter(id => !notFoundIDs.includes(id));
                    }
                }
            }
        } else {
            for (const row of this.dataRows) {
                const rowDataKey = row.dataKey.toString();
                row.isSelected = this._selectionHelper.contains(rowDataKey);
            }
        }

        if (this.selectedRows.length > 0) {
            this._lastClickedRow = this.selectedRows[0]; // nastavuji pouze field, protoze setter nastavuje keyPressRowIndex, ktery chci zachovat
            if (this._refreshSelection) {
                this.dispatchSelectionChanged();
                this._refreshSelection = false;
            }
        } else {
            if (IDs.length > 0 && !(fromLastSelectedRows && this._lastSelectedDataKeys.length == 0)) {
                this._clearLastSelectedDataKeys();
                this._dispatchSelectionChangeEvent(IDs[0], { doubleClick: false, clickedRow: this.lastClickedRow });
            }
        }
    }

    public removeIsChangedAll(): void {
        this._allDataRows.forEach(row => {
            if (row.isChanged) {
                row.isChanged = false;
            }
        });

        this.changedRowsID = [];
    }

    public removeIsChangedOnKey(key: any): void {
        this._allDataRows.forEach(row => {
            if (row.dataKey === key) {
                row.isChanged = false;
            }
        });
        this.changedRowsID = this.changedRowsID.filter(rowID => rowID !== key);
    }

    public override nextRecord(trigger = true): void {
        super.nextRecord(trigger);
        if (this.isInEdit) {
            this._lastClickedRow.isInEdit = true;
        }
    }

    public override endNextRecord(): void {
        super.endNextRecord();
        if (this.isInEdit) {
            this._lastClickedRow.isInEdit = true;
        }
    }

    public override homePrevRecord(): void {
        super.homePrevRecord();
        if (this.isInEdit) {
            this._lastClickedRow.isInEdit = true;
        }
    }

    public override pageDownNextRecord(): void {
        super.pageDownNextRecord();
        if (this.isInEdit) {
            this._lastClickedRow.isInEdit = true;
        }
    }

    public override pageUpPrevRecord(): void {
        super.pageUpPrevRecord();
        if (this.isInEdit) {
            this._lastClickedRow.isInEdit = true;
        }
    }

    public override prevRecord(trigger = true): void {
        super.prevRecord(trigger);
        if (this.isInEdit) {
            this._lastClickedRow.isInEdit = true;
        }
    }

    /**
     * Metoda pro zobrazeni tlacitka Zobrazi a vyvolani eventu
     * @param display zobrazeni/schovani tlacitka Zobrazit
     * @param enableEvent povoleni spusteni eventu
     */
    public displayEditedRows(): void {
        this.displayedEditedRows = true;
        if (this.emptyMessage.messageType !== EmptyDatagridMessageType.queryModeEmpty) {
            this._displayEditedRowsButton.style.display = "none";
            this._displayAllRowsButton.style.removeProperty("display");
        }

        let selectionChange = false;

        this.dataRows.filter((row) => {
            return !row.isChanged;
        }).forEach((row) => {
            if (row.isSelected) {
                if (!selectionChange)
                    selectionChange = true;
                row.isSelected = false;
            }
            row.visible = false;
        });

        if (selectionChange) {
            this.dispatchSelectionChanged();
        }
    }

    private _removeRowFromEdited(ID: any): void {
        const row = this.editedRows.find((item) => item.dataKey == ID);
        if (row) {
            this.editedRows.splice(this.editedRows.indexOf(row), 1);
        }
    }

    /**
     * Metoda pro zobrazeni tlacitka Zobrazit vse a vyvolani eventu
     * @param display zobrazeni/schovani tlacitka Zobrazit vse
     */
    public displayAllRows(): void {
        this.displayedEditedRows = false;
        if (this.emptyMessage.messageType !== EmptyDatagridMessageType.queryModeEmpty) {
            this._displayAllRowsButton.style.display = "none";
            this._displayEditedRowsButton.style.removeProperty("display");
        }

        this._allDataRows.forEach(row => {
            if (!row.visible) {
                row.visible = true;
            }
        });
    }

    public dispatchSelectionChanged(): void {
        if (this.selectedRows.length == 1) {
            this._dispatchSelectionChangeEvent(this.lastClickedRow.dataKey, { doubleClick: false, clickedRow: this.lastClickedRow });
        } else {
            this._dispatchSelectionChangeEvent("-1", { doubleClick: false, clickedRow: this.lastClickedRow });
        }
    }

    /**
     * Zobrazeni/schovani divu s poctem aktualne rozeditovanych zaznamu
     * @param display true = zobrazeni divu, false = schovani divu
     */
    public displayCountBar(display: boolean): void {
        if (this.displayEditedCountBar === false) {
            return;
        }
        if (display) {
            if (this.emptyMessage.messageType === EmptyDatagridMessageType.queryModeEmpty) {
                this._displayEditedRowsButton.style.display = "none";
            } else {
                this._displayEditedRowsButton.style.removeProperty("display");
            }

            this._editedRowCountElement.style.removeProperty("display");
        } else {
            this._editedRowCountElement.style.display = "none";
            this.displayAllRows();
        }
    }

    /**
     * Snizeni poctu rozeditovanych zaznamu o 1
     */
    public decEditedRows(): void {
        if (this._editedRowCount > 0) {
            this._editedRowCount = --this._editedRowCount;
        }
        if (this._editedRowCount == 0) {
            this.displayCountBar(false);
        }
    }

    /**
     * Zvyseni poctu rozeditovanych zaznamu o 1
     */
    public incEditedRows(): void {
        if (this._editedRowCount == 0) {
            this.displayCountBar(true);
        }
        this._editedRowCount = ++this._editedRowCount;
    }

    /**
     * Update poctu zobrazenych zaznamu
     * @param count pocet zaznamu
     */
    private _updateCountBar(count: number): void {
        this._editedRowCount = count;
    }

    /**
     * Vytvoreni elementu, ktery zobrazuje pocet aktualne rozeditovanych zaznamu
     */
    private _renderCountBar(): void {
        this._editedRowCountElement = document.createElement("div");
        this._displayEditedRowsButton = document.createElement("div");
        this._editedRowsCounterEl = document.createElement("label");
        this._numberOfEditeRowsEl = document.createElement("label");
        this._displayAllRowsButton = document.createElement("div");

        this._displayAllRowsButton.classList.add("DisplayAllRow", "btn", "flat");
        this._displayEditedRowsButton.classList.add("DisplayEditedRows", "btn", "flat");
        this._editedRowCountElement.classList.add("EditContainer");
        this._editedRowsCounterEl.classList.add("Counter");
        this._numberOfEditeRowsEl.classList.add("Description");

        Localization.addLocData(this._displayEditedRowsButton, "GUI-451280");
        Localization.addLocData(this._numberOfEditeRowsEl, "GUI-523295");
        Localization.addLocData(this._displayAllRowsButton, "GUI-452722");

        this._displayAllRowsButton.style.display = "none";

        this._editedRowCountElement.appendChild(this._numberOfEditeRowsEl);
        this._editedRowCountElement.appendChild(this._editedRowsCounterEl);
        this._editedRowCountElement.appendChild(this._displayEditedRowsButton);
        this._editedRowCountElement.appendChild(this._displayAllRowsButton);

        this._eventsController.addListener(this._displayAllRowsButton, "click", (_e) => {
            if (this._displayAllRowsButton.classList.contains("Disabled")) {
                return;
            }
            this.displayAllRows();
        });
        this._eventsController.addListener(this._displayEditedRowsButton, "click", (_e) => {
            if (this._displayEditedRowsButton.classList.contains("Disabled")) {
                return;
            }
            this.displayEditedRows();
        });

        this._editedRowCount = 0;
        this._editedRowCountElement.style.display = "none";

        this._mainListDiv.appendChild(this._editedRowCountElement);
    }

    /**
     * Vytvori query mode banner
     */
    private _renderQueryModeBanner(): void {
        this._queryModeContainer = document.createElement("div");
        const queryModeDescript = document.createElement("label");
        this._queryModeButton = document.createElement("div");

        this._queryModeContainer.classList.add("EditContainer");
        queryModeDescript.classList.add("Description");
        this._queryModeButton.classList.add("DisplayEditedRows", "btn", "flat");

        Localization.addLocData(queryModeDescript, "GUI-696397"); // Pro zobrazení více dat nastavte filtr
        Localization.addLocData(this._queryModeButton, "GUI-676719"); // Načíst všechna data

        this._queryModeContainer.appendChild(queryModeDescript);
        this._queryModeContainer.appendChild(this._queryModeButton);

        this._queryModeContainer.style.display = "none";

        this._mainListDiv.appendChild(this._queryModeContainer);

        this.dataSource.loadingStarted.add(() => {
            this._displayQueryModeBanner(false);
            this._queryModeContainer.remove();
        });
    }

    /**
     * Metoda vytvori query mode banner a zaregistruje udalosti, ktere ukoncuji query mode
     * @param key klíč, pod kterým se uloží listener
     * @param dataLoad funkce refreshData
     */
    public registerLoadDataQueryMode(dataLoad: Function): void {
        if (this.emptyMessage.messageType === EmptyDatagridMessageType.queryModeEmpty && !this._queryModeContainer) {
            this._renderQueryModeBanner();
        }

        this._eventsController.addListenerWithKey(`${LOAD_QUERY_MODE_EVENT}click`, this._queryModeButton, "click", () => dataLoad(), { removePrevious: true });
        this._eventsController.addListenerWithKey(`${LOAD_QUERY_MODE_EVENT}order`, this, "OrderingChanged", () => dataLoad(), { removePrevious: true });
    }

    /**
     * Metoda odregistruje události query mode
     * @param key klíč, pod kterým je uložen listener
     */
    public unregisterLoadDataQueryMode(): void {
        this._eventsController.removeByKey(`${LOAD_QUERY_MODE_EVENT}click`);
        this._eventsController.removeByKey(`${LOAD_QUERY_MODE_EVENT}order`);
    }

    /**
     * Zobrazi/skryje query mode banner, pokud je zapnuty query mode
     */
    private _displayQueryModeBanner(display: boolean): void {
        if (this._queryModeContainer) {
            if (display) {
                this._queryModeContainer.style.removeProperty("display");
            } else {
                this._queryModeContainer.style.display = "none";
            }
        }
    }

    /**
     * Znovu vykreslit datove radky
     */
    public override async reRenderData(): Promise<void> {
        this._updateCountBar(0); // CountBar je nutne vynulovat, protoze v ramci rerenderu se znovu vyhodnocuje vlastnost isChanged jednotlivych radku (viz nize), ktera zpusobuje jeho inkrementaci

        await super.reRenderData();
        for (const row of this.dataRows) {
            if (this.changedRowsID.includes(row.dataKey)) {
                row.isChanged = true;
            }
        }
    }
}

/**
 * Obsahuje informace o tom, která práva se na komponentě změnila
 */
export interface IPermissionChanges {
    /**
     * Komponenta, o kterou se jedná
     */
    control: IValidatable;
    /**
     * Zda se změnila povinnost
     */
    required: boolean;
}

registerCustomElement("tsc-editabledatagrid", EditableDataGrid);
